/**
 * Listens for MFA enrollment challenges raised by any API fetcher.
 *
 * The fetchers are plain modules with no access to the router, so they
 * announce a challenge as a window event (see services/mfaChallenge.ts) and
 * this hook turns it into something a component can redirect on.
 *
 * WHY useSyncExternalStore AND NOT useEffect
 * ------------------------------------------
 * A challenge is usually raised by a fetch that a page fired on mount, which
 * can settle before the route guard has subscribed. Subscribing in an effect
 * and replaying the missed value would mean calling setState inside the effect
 * body - a cascading render, and the thing eslint's react-hooks rules reject.
 *
 * useSyncExternalStore is built for this: `getSnapshot` is read on every
 * render, so a challenge that arrived before subscription is picked up without
 * any replay, and `subscribe` handles everything after.
 *
 * The snapshot must be referentially stable or React re-renders forever, so
 * `current` is only ever reassigned when a new challenge actually arrives.
 */

import { useSyncExternalStore } from 'react';
import {
  MFA_CHALLENGE_EVENT,
  MFA_CHALLENGE_CLEARED_EVENT,
  type MfaChallenge,
  type MfaChallengeEventDetail,
} from '../services/mfaChallenge';

let current: MfaChallenge | null = null;
const subscribers = new Set<() => void>();

if (typeof window !== 'undefined') {
  // Registered at module load, i.e. before any component mounts, so a
  // challenge raised during the very first render is not lost.
  window.addEventListener(MFA_CHALLENGE_EVENT, (event) => {
    const detail = (event as CustomEvent<MfaChallengeEventDetail>).detail;
    if (!detail?.challenge) return;
    current = detail.challenge;
    subscribers.forEach((notify) => notify());
  });

  // Revoking matters as much as raising. Without this, a user who enrolled
  // successfully would be redirected back to enrollment by the route guard
  // forever - see MFA_CHALLENGE_CLEARED_EVENT.
  window.addEventListener(MFA_CHALLENGE_CLEARED_EVENT, () => {
    clearMfaChallenge();
  });
}

function subscribe(notify: () => void): () => void {
  subscribers.add(notify);
  return () => {
    subscribers.delete(notify);
  };
}

function getSnapshot(): MfaChallenge | null {
  return current;
}

/** Clear the remembered challenge, e.g. once enrollment has completed. */
export function clearMfaChallenge(): void {
  if (current === null) return;
  current = null;
  subscribers.forEach((notify) => notify());
}

/** For tests: read what was last recorded without mounting a component. */
export function peekMfaChallenge(): MfaChallenge | null {
  return current;
}

export function useMfaChallenge(): MfaChallenge | null {
  // Same snapshot on the server as on the client: no challenge is known until
  // a fetch has actually been refused.
  return useSyncExternalStore(subscribe, getSnapshot, () => null);
}
